import { useEffect, useState } from "react";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useLang } from "@/context/LangContext";
import { useSettings } from "@/context/SettingsContext";
import { ContentSettings, MediaSettings } from "@/components/admin/ContentSettings";
import { PackagesManager } from "@/components/admin/PackagesManager";
import { SystemSettings } from "@/components/admin/SystemSettings";
import { PaymentSettings, ContactSettings } from "@/components/admin/PaymentSettings";
import { PageLoader } from "@/components/ProtectedRoute";
import { api, formatApiError } from "@/lib/api";

const TABS = ["content", "media", "packages", "payment", "contact", "system"];

export const SettingsPanel = () => {
  const { t } = useLang();
  const { settings, setSettings } = useSettings();
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (settings && !draft) setDraft(structuredClone(settings)); }, [settings, draft]);
  if (!draft) return <PageLoader />;

  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/settings", draft);
      setSettings(data);
      setDraft(structuredClone(data));
      toast.success(t("saved"));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-2xl border border-border bg-card p-5 sm:p-8" data-testid="admin-settings-panel">
      <Tabs defaultValue="content">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <TabsList className="h-auto flex-wrap justify-start">
            {TABS.map((k) => <TabsTrigger key={k} value={k} data-testid={`admin-settings-tab-${k}`}>{t(`settings_${k}`)}</TabsTrigger>)}
          </TabsList>
          <button onClick={save} disabled={busy} data-testid="admin-settings-save-button" className="inline-flex items-center justify-center gap-2 rounded-full bg-primary px-6 py-2.5 font-bold text-primary-foreground transition-transform hover:scale-[1.02] disabled:opacity-60">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}{t("save")}
          </button>
        </div>
        <TabsContent value="content" className="mt-8"><ContentSettings draft={draft} set={set} /></TabsContent>
        <TabsContent value="media" className="mt-8"><MediaSettings draft={draft} set={set} /></TabsContent>
        <TabsContent value="packages" className="mt-8">
          <PackagesManager packages={draft.packages} onChange={(v) => set("packages", v)} />
        </TabsContent>
        <TabsContent value="payment" className="mt-8"><PaymentSettings draft={draft} set={set} /></TabsContent>
        <TabsContent value="contact" className="mt-8"><ContactSettings draft={draft} set={set} /></TabsContent>
        <TabsContent value="system" className="mt-8">
          <SystemSettings draft={draft} set={set} />
        </TabsContent>
      </Tabs>
    </div>
  );
};
